import { useShopHandlers } from "./useShopHandlers";
import { useSearchParams } from "./useSearchParams";
import { useShopContext } from "../context/ShopContext/ShopContext";
import { ProductTaxonomyEnum } from "../../types/generalTypes";
import { useAttributeFiltersQuery } from "./useAttributeFiltersQuery";
import * as shopQueries from "../../queries/shop";
import { ShopSelectOptions } from "../../components/Shop/ui/ShopSelect/ShopSelect";
import { useAuthContext } from "../context/AuthContext/AuthContext";
import { useMemo } from "react";
import { usePersistedQuery } from "./usePersistedQuery";
import {
  persistKeyFromVariables,
  QUERY_TTL,
} from "../helpers/persistedQueryCache";

export const getTaxonomyByShopType = (type?: "category" | "brand") => {
  switch (type) {
    case "category":
      return ProductTaxonomyEnum.PRODUCTCATEGORY;
    case "brand":
      return ProductTaxonomyEnum.PRODUCTBRAND;
    default:
      return ProductTaxonomyEnum.PRODUCTCATEGORY;
  }
};

export interface ShopProductCategoryCustom {
  id: string;
  databaseId: number;
  link: string;
  slug: string;
  name: string;
  description: string;
  image: {
    id: string;
    sourceUrl: string;
    databaseId: number;
    mediaItemUrl: string;
  };
  children: {
    nodes: ShopProductCategoryCustom[];
  };
}

export interface GetShopProductCategoriesResponseCustom {
  productCategories: {
    nodes: ShopProductCategoryCustom[];
  };
}

export const useShopFilterData = () => {
  const shopCtx = useShopContext();
  const shopHandlers = useShopHandlers();
  const searchParams = useSearchParams();
  const taxonomies = shopHandlers.prepareTaxonomyFilters(searchParams);
  const brand = searchParams["brand"];
  const customTaxonomy = [
    ...taxonomies,
    ...(brand
      ? [
          {
            taxonomy: ProductTaxonomyEnum.PRODUCTBRAND,
            terms: [brand as string],
          },
        ]
      : []),
  ];

  const taxonomy = getTaxonomyByShopType(
    shopCtx?.data?.shopType
  ) as ProductTaxonomyEnum;

  const attributes = useAttributeFiltersQuery(
    shopCtx?.data?.shopType === "brand" && shopCtx?.data?.slug
      ? {
          taxonomy: taxonomy,
          terms: shopCtx?.data?.slug,
        }
      : undefined
  );

  const categoryQueryVariables =
    customTaxonomy.length > 0 ? { customTaxonomy } : undefined;

  const { loading, data, error, refetch: refetchProductCategories } = usePersistedQuery<
    GetShopProductCategoriesResponseCustom,
    shopQueries.GetShopProductCategoriesVariables
  >(shopQueries.getProductCategories, {
    variables: categoryQueryVariables,
    persistKey: persistKeyFromVariables(
      "productCategories",
      categoryQueryVariables
    ),
    persistTtl:
      customTaxonomy.length > 0 ? QUERY_TTL.attributeFilters : QUERY_TTL.categories,
  });

  const handleAttributesOnChange = (
    attribute: string,
    selectedValue?: ShopSelectOptions
  ) => {
    shopHandlers.updateSearchParameters(
      selectedValue?.value || "",
      attribute,
      (data: any) => {
        const taxonomies = shopHandlers.prepareTaxonomyFilters(data);
        refetchProductCategories({
          customTaxonomy: [
            ...taxonomies,
            ...(brand
              ? [
                  {
                    taxonomy: ProductTaxonomyEnum.PRODUCTBRAND,
                    terms: [brand as string],
                  },
                ]
              : []),
          ],
        });
        if (shopCtx?.handlers?.handleAttributeChange) {
          shopCtx.handlers.handleAttributeChange(data);
        }
      }
    );
  };

  const handleCategoryChange = (categorySlug: string) => {
    if (shopCtx.handlers?.handleCategoryChange) {
      shopCtx.handlers.handleCategoryChange(categorySlug);
    }
  };

  const authCtx = useAuthContext();

  // Filter categories based on user's showCategories
  const categories = useMemo(() => {
    const rawCategories = data?.productCategories?.nodes ?? [];
    
    // If user is not authenticated, show all categories
    if (!authCtx.isAuthenticated || !authCtx.user) {
      return rawCategories;
    }

    // If showCategories is empty or undefined, show all categories
    const showCategories = authCtx.user.showCategories;
    if (!showCategories || showCategories.length === 0) {
      return rawCategories;
    }

    // Filter categories based on showCategories
    const allowedCategoryIds = showCategories
      .filter((cat) => cat && cat.databaseId != null)
      .map((cat) => cat.databaseId.toString());
    
    const filterCategoryTree = (cats: ShopProductCategoryCustom[], parentAllowed: boolean = false): ShopProductCategoryCustom[] => {
      return cats.filter(category => {
        if (!category || category.databaseId == null) {
          return false;
        }

        // If parent is allowed, include all children
        if (parentAllowed) {
          return true;
        }
        
        // Check if this category should be shown
        if (allowedCategoryIds.includes(category.databaseId.toString())) {
          return true;
        }
        
        // Check if any child categories should be shown
        if (category.children?.nodes?.length > 0) {
          const filteredChildren = filterCategoryTree(category.children.nodes, false);
          if (filteredChildren.length > 0) {
            // Include this category if it has allowed children
            return true;
          }
        }
        
        return false;
      }).map(category => {
        const categoryAllowed = allowedCategoryIds.includes(category.databaseId.toString());
        return {
          ...category,
          children: category.children?.nodes?.length > 0 ? {
            nodes: filterCategoryTree(category.children.nodes, categoryAllowed || parentAllowed)
          } : category.children
        };
      });
    };

    return filterCategoryTree(rawCategories);
  }, [data?.productCategories?.nodes, authCtx.isAuthenticated, authCtx.user?.showCategories]);

  const categoryOptions = categories?.map((cat: ShopProductCategoryCustom) => ({
    value: cat.slug,
    label: cat.name,
    icon: cat.image?.mediaItemUrl,
    description: cat.description,
  })) as ShopSelectOptions[];

  const selectedCategory = categoryOptions.find(
    (c) => c.value === shopCtx?.data?.category
  );

  const subCategoryOptions = shopCtx?.data?.category
    ? categories
        .find((cat: ShopProductCategoryCustom) => cat.slug === shopCtx?.data?.category)
        ?.children?.nodes?.map((cat: ShopProductCategoryCustom) => ({
          value: cat.slug,
          label: cat.name,
          icon: cat.image?.mediaItemUrl,
          description: cat?.description,
        }))
    : [];

  const subSubCategoryOptions = shopCtx?.data?.category
    ? categories
        .find((cat1: ShopProductCategoryCustom) => cat1.slug === shopCtx?.data?.category)
        ?.children?.nodes?.find(
          (cat2: ShopProductCategoryCustom) => cat2.slug === shopCtx?.data?.subCategory
        )
        ?.children?.nodes?.map((cat: ShopProductCategoryCustom) => ({
          value: cat.slug,
          label: cat.name,
          icon: cat.image?.mediaItemUrl,
          description: cat?.description,
        }))
    : [];

  const subSubSubCategoryOptions = shopCtx?.data?.category
    ? categories
        .find((cat1: ShopProductCategoryCustom) => cat1.slug === shopCtx?.data?.category)
        ?.children?.nodes?.find(
          (cat2: ShopProductCategoryCustom) => cat2.slug === shopCtx?.data?.subCategory
        )
        ?.children?.nodes?.find(
          (cat3: ShopProductCategoryCustom) => cat3.slug === shopCtx?.data?.subSubCategory
        )
        ?.children?.nodes?.map((cat4: ShopProductCategoryCustom) => ({
          value: cat4.slug,
          label: cat4.name,
          icon: cat4.image?.mediaItemUrl,
          description: cat4?.description,
        }))
    : [];

  const selectedSubCategory = subCategoryOptions?.find(
    (sc) => sc.value === shopCtx?.data?.subCategory
  );

  const selectedSubSubCategory = subSubCategoryOptions?.find(
    (sc) => sc.value === shopCtx?.data?.subSubCategory
  );

  const selectedSubSubSubCAtegory = subSubSubCategoryOptions?.find(
    (sc) => sc.value === shopCtx?.data?.subSubSubCategory
  );

  const redirectLinkPrefix = shopCtx?.data?.shopLinkPrefix
    ? `${shopCtx?.data?.shopLinkPrefix}`
    : "/shop";

  const attributesData = attributes?.data?.attributeFilters ?? [];

  return {
    error,
    loading,
    categories,
    attributes,
    attributesData,
    categoryOptions,
    selectedCategory,
    redirectLinkPrefix,
    subCategoryOptions: {
      subCategoryOptions: subCategoryOptions,
      description: subCategoryOptions ? subCategoryOptions[0]?.description : [],
    },
    selectedSubCategory,
    handleCategoryChange,
    subSubCategoryOptions: {
      subSubCategoryOptions: subSubCategoryOptions,
      description: subCategoryOptions ? subCategoryOptions[0]?.description : [],
    },
    selectedSubSubCategory,
    subSubSubCategoryOptions: {
      description: subCategoryOptions ? subCategoryOptions[0]?.description : [],
      subSubSubCategoryOptions: subSubSubCategoryOptions,
    },
    handleAttributesOnChange,
    selectedSubSubSubCAtegory,
  };
};
